import {
  clearAuthData,
  getAccessToken,
  getRefreshToken,
  getUserId,
  isLoginAsSession,
  restoreMainAuthFromLoginAs,
  setAuthTokens,
} from './authData';
import { getEnvironmentConfig } from './environment';

interface IRefreshResponse {
  accessToken: string;
  refreshToken: string;
}

interface IRequestLike {
  url?: string;
  method?: string;
}

const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/sign-in/web',
  '/auth/sign-in/mob',
  '/auth/sign-in/admin',
  '/auth/sign-up',
  '/auth/sign-up/web',
  '/auth/sign-up/mob',
  '/auth/resend-verification-email',
  '/auth/send-password-reset',
  '/auth/password-reset',
  '/auth/verify-email',
  '/auth/one-time-entry',
  '/auth/refresh',
  '/auth/logout',
];
const PUBLIC_FILE_CABINET_ENDPOINTS = ['/file-cabinet/public'];

const SIGNED_URL_QUERY_KEYS = [
  'X-Amz-Algorithm',
  'X-Amz-Credential',
  'X-Amz-Signature',
  'X-Amz-Security-Token',
  'AWSAccessKeyId',
  'Signature',
  'Expires',
];

const { apiUrl } = getEnvironmentConfig();

let refreshPromise: Promise<string> | null = null;

const AUTH_REFRESH_STATE_CHANGED_ERROR_NAME = 'AuthRefreshStateChangedError';

function getRequestUrl(request: string | IRequestLike | undefined): string | undefined {
  if (!request) {
    return undefined;
  }

  return typeof request === 'string' ? request : request.url;
}

function getAuthUrl(path: string): string {
  if (!apiUrl) {
    return `/${path}`;
  }

  return `${apiUrl}${apiUrl.endsWith('/') ? '' : '/'}${path}`;
}

function normalizePath(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return normalizedPath.length > 1 && normalizedPath.endsWith('/')
    ? normalizedPath.slice(0, -1)
    : normalizedPath;
}

function isBuilderPadUrl(url: URL): boolean {
  const apiHostname = apiUrl ? new URL(apiUrl).hostname : '';

  return (
    url.hostname === apiHostname ||
    url.hostname === 'builderpad.com' ||
    url.hostname.endsWith('.builderpad.com')
  );
}

function isSignedUrl(url: URL): boolean {
  return SIGNED_URL_QUERY_KEYS.some((key) => url.searchParams.has(key));
}

function isRefreshResponse(data: unknown): data is IRefreshResponse {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const response = data as Partial<IRefreshResponse>;

  return typeof response.accessToken === 'string' && typeof response.refreshToken === 'string';
}

function getRefreshError(message: string): Error {
  return new Error(message);
}

function getRefreshStateChangedError(): Error {
  const error = new Error('Auth state changed during refresh');
  error.name = AUTH_REFRESH_STATE_CHANGED_ERROR_NAME;

  return error;
}

export function isAuthRefreshStateChangedError(error: unknown): boolean {
  return error instanceof Error && error.name === AUTH_REFRESH_STATE_CHANGED_ERROR_NAME;
}

export function assertAuthStateMatchesRefreshedAccessToken(accessToken: string): void {
  if (isLoginAsSession() || getAccessToken() !== accessToken) {
    throw getRefreshStateChangedError();
  }
}

export function getLatestAccessTokenAfterAuthChange(accessToken: string | null): string | null {
  const latestAccessToken = getAccessToken();

  if (!accessToken || !latestAccessToken || isLoginAsSession() || latestAccessToken === accessToken) {
    return null;
  }

  return latestAccessToken;
}

function getAccessTokenPayload(accessToken: string | null): { sid?: string; sub?: string | number } | null {
  if (!accessToken) {
    return null;
  }

  const token = accessToken.replace(/^Bearer\s+/i, '');
  const payload = token.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload =
      normalizedPayload + '='.repeat((4 - (normalizedPayload.length % 4)) % 4);

    return JSON.parse(window.atob(paddedPayload));
  } catch {
    return null;
  }
}

export function getAuthSessionIdentity(accessToken: string | null = getAccessToken()): string | null {
  const tokenPayload = getAccessTokenPayload(accessToken);
  const userId = getUserId() ?? tokenPayload?.sub?.toString() ?? null;
  const sessionId = tokenPayload?.sid ?? null;

  if (!userId && !sessionId) {
    return null;
  }

  return `${userId ?? ''}:${sessionId ?? ''}`;
}

export function hasAuthSessionIdentityChanged(requestIdentity: string | null): boolean {
  const currentIdentity = getAuthSessionIdentity();

  return !!requestIdentity && !!currentIdentity && requestIdentity !== currentIdentity;
}

export function shouldSkipRefresh(request: string | IRequestLike | undefined): boolean {
  const requestUrl = getRequestUrl(request);

  if (!requestUrl) {
    return false;
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(requestUrl, apiUrl || 'https://builderpad.com');
  } catch {
    return true;
  }

  const isAbsoluteUrl = /^https?:\/\//i.test(requestUrl);

  if (isAbsoluteUrl && !isBuilderPadUrl(parsedUrl)) {
    return true;
  }

  if (isSignedUrl(parsedUrl)) {
    return true;
  }

  const normalizedPath = normalizePath(parsedUrl.pathname);

  return (
    PUBLIC_AUTH_ENDPOINTS.includes(normalizedPath) ||
    PUBLIC_FILE_CABINET_ENDPOINTS.some((endpoint) => normalizedPath.startsWith(endpoint))
  );
}

async function executeRefreshAccessToken(): Promise<string> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (isLoginAsSession()) {
    throw getRefreshStateChangedError();
  }

  if (!accessToken || !refreshToken) {
    throw getRefreshError('Missing auth tokens for refresh');
  }

  const response = await fetch(getAuthUrl('auth/refresh'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    throw getRefreshError('Auth refresh failed');
  }

  const data: unknown = await response.json();

  if (!isRefreshResponse(data)) {
    throw getRefreshError('Auth refresh response is invalid');
  }

  if (
    isLoginAsSession() ||
    getAccessToken() !== accessToken ||
    getRefreshToken() !== refreshToken
  ) {
    throw getRefreshStateChangedError();
  }

  setAuthTokens(data);

  return getAccessToken() ?? `Bearer ${data.accessToken}`;
}

export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = executeRefreshAccessToken();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export function clearAuthAndRedirectToSignIn(): boolean {
  if (isLoginAsSession() && restoreMainAuthFromLoginAs()) {
    if (typeof window !== 'undefined') {
      window.location.replace('/account-dashboard');
    }

    return true;
  }

  clearAuthData();

  if (typeof window !== 'undefined' && window.location.pathname !== '/sign-in') {
    window.location.replace('/sign-in');
  }

  return false;
}

export function clearAuthAndRedirectAfterRefreshFailure(error: unknown): boolean {
  if (isAuthRefreshStateChangedError(error)) {
    return false;
  }

  return clearAuthAndRedirectToSignIn();
}

export function resolveLoginAsRequestAuthFailure(): boolean {
  if (isLoginAsSession()) {
    return clearAuthAndRedirectToSignIn();
  }

  return true;
}

export async function logoutSession(): Promise<void> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return;
  }

  try {
    await fetch(getAuthUrl('auth/logout'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {}
}
